import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { RequireOrg, VpState } from '../components/VelopulseShell';
import VpModal from '../components/VpModal';
import useLoad from '../utils/useLoad';
import { useOrgStore } from '../store/org';
import { vpApi, errorMessage, fmtMoney, fmtNum, fmtRelDay, VEHICLE_STATUS, vehicleTitle } from '../api/velopulse';

// R1 僅「車輛庫存」一種類型；其餘類型於 R3 開放
const TYPES = [
  { key: 'vehicle', label: '車輛庫存', enabled: true },
  { key: 'customer', label: '客戶', enabled: false },
  { key: 'supplier', label: '供應商', enabled: false },
];

const EMPTY_VEHICLE = { brand: '', model: '', model_year: '', exterior_color: '', mileage: '', asking_price: '', plate_no: '' };

function NewVehicleModal({ onClose, onCreated }) {
  const org = useOrgStore((s) => s.currentOrg);
  const [form, setForm] = useState(EMPTY_VEHICLE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const num = (v) => (v === '' ? null : Number(v));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.brand.trim() || !form.model.trim()) { setError('請填寫廠牌與車款。'); return; }
    setSaving(true); setError('');
    try {
      const created = await vpApi.createRecord({ ...form, model_year: num(form.model_year), mileage: num(form.mileage), asking_price: num(form.asking_price) });
      toast.success(`已建立車輛 ${created.code || ''}`);
      onCreated(created);
    } catch (err) {
      setError(errorMessage(err, '建立失敗，請檢查欄位後再試。'));
    } finally { setSaving(false); }
  };

  return (
    <VpModal title="新增車輛" onClose={onClose}>
      <form onSubmit={submit}>
        <p className="vp-muted" style={{ fontSize: 12, margin: 0 }}>將建立於組織：<strong style={{ color: 'var(--vp-ink)' }}>{org?.name}</strong></p>
        <div className="vp-row2">
          <div className="vp-field"><label htmlFor="nv-brand">廠牌</label><input id="nv-brand" className="vp-input" value={form.brand} onChange={set('brand')} placeholder="例如：Toyota" /></div>
          <div className="vp-field"><label htmlFor="nv-model">車款</label><input id="nv-model" className="vp-input" value={form.model} onChange={set('model')} placeholder="例如：Camry 2.0" /></div>
          <div className="vp-field"><label htmlFor="nv-year">年式</label><input id="nv-year" className="vp-input" type="number" value={form.model_year} onChange={set('model_year')} /></div>
          <div className="vp-field"><label htmlFor="nv-color">顏色</label><input id="nv-color" className="vp-input" value={form.exterior_color} onChange={set('exterior_color')} /></div>
          <div className="vp-field"><label htmlFor="nv-km">里程（公里）</label><input id="nv-km" className="vp-input" type="number" value={form.mileage} onChange={set('mileage')} /></div>
          <div className="vp-field"><label htmlFor="nv-price">標價（NT$）</label><input id="nv-price" className="vp-input" type="number" value={form.asking_price} onChange={set('asking_price')} /></div>
          <div className="vp-field"><label htmlFor="nv-plate">車牌</label><input id="nv-plate" className="vp-input" value={form.plate_no} onChange={set('plate_no')} /></div>
        </div>
        {error && <div className="vp-error" role="alert">{error}</div>}
        <div className="vp-wiz-actions">
          <button type="button" className="vp-btn lg" onClick={onClose}>取消</button>
          <button type="submit" className="vp-btn lg primary" disabled={saving}>{saving ? '建立中…' : '建立車輛'}</button>
        </div>
      </form>
    </VpModal>
  );
}

function RecordsLibrary() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState('');
  const [kw, setKw] = useState('');
  const [status, setStatus] = useState('');
  const [sort, setSort] = useState('updated_desc');
  const showNew = params.get('new') === '1';

  // 搜尋輸入防抖
  useEffect(() => { const t = setTimeout(() => setKw(q.trim()), 300); return () => clearTimeout(t); }, [q]);

  const { data: rows, error, loading, reload } = useLoad(() => vpApi.listRecords({ type: 'vehicle', q: kw, status, sort }), [kw, status, sort]);
  const filtered = !!(kw || status);
  const closeNew = () => { params.delete('new'); setParams(params, { replace: true }); };

  return (
    <div className="vp-content wide">
      <div className="vp-tabs" role="tablist" aria-label="紀錄類型">
        {TYPES.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={t.key === 'vehicle'} className={t.key === 'vehicle' ? 'on' : ''} disabled={!t.enabled} title={t.enabled ? undefined : '此類型將於後續版本開放'}>
            {t.label}
          </button>
        ))}
        <button type="button" disabled title="自訂紀錄類型將於後續版本開放">＋ 類型</button>
      </div>

      <div className="vp-bar">
        <input type="search" placeholder="搜尋車款、車牌、編號…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="搜尋紀錄" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="狀態篩選">
          <option value="">狀態：全部</option>
          {Object.entries(VEHICLE_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="排序">
          <option value="updated_desc">排序：最近更新</option>
          <option value="price_desc">標價高→低</option>
        </select>
        <button type="button" className="vp-btn primary" onClick={() => setParams({ new: '1' })}>新增車輛</button>
      </div>

      <div className="vp-list-card">
        {loading && <VpState loading />}
        {!loading && error && <VpState text={errorMessage(error, '紀錄載入失敗。')} action={{ label: '重新載入', onClick: reload }} />}
        {!loading && !error && rows?.length === 0 && (
          filtered
            ? <VpState text="沒有符合條件的車輛，請調整搜尋或篩選。" action={{ label: '清除篩選', onClick: () => { setQ(''); setStatus(''); } }} />
            : <VpState text="這個組織還沒有車輛紀錄，先新增第一輛車吧。" action={{ label: '新增車輛', onClick: () => setParams({ new: '1' }) }} />
        )}
        {!loading && !error && rows?.length > 0 && (
          <table className="vp-table">
            <thead><tr><th>編號</th><th>車款</th><th>年式</th><th>顏色</th><th>里程</th><th>狀態</th><th>標價</th><th>更新</th></tr></thead>
            <tbody>
              {rows.map((r) => {
                const st = VEHICLE_STATUS[r.status];
                return (
                  <tr key={r.id} tabIndex={0} onClick={() => navigate(`/records/${r.id}`)} onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/records/${r.id}`); }}>
                    <td>{r.code}</td><td>{vehicleTitle(r)}</td><td>{r.model_year || '—'}</td><td>{r.exterior_color || '—'}</td><td>{fmtNum(r.mileage)}</td>
                    <td>{st ? <span className={`vp-badge ${st.tone}`}>{st.label}</span> : '—'}</td>
                    <td className="vp-price">{fmtMoney(r.asking_price)}</td><td>{fmtRelDay(r.updated_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showNew && <NewVehicleModal onClose={closeNew} onCreated={(r) => { closeNew(); if (r?.id) navigate(`/records/${r.id}`); else reload(); }} />}
    </div>
  );
}

export default function RecordsLibraryPage() {
  return <RequireOrg><RecordsLibrary /></RequireOrg>;
}
